import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { Color } from '../../../assets/color';
import { useNavigate } from 'react-router-dom';
import RouteHeader from '../../../components/common/RouteHeader';
import DatatableHeading from '../../../components/heading/DatatableHeading';
import * as SuperadminActions from '../../../redux/actions/superadminAction';
import { Eye, Trash2, Clock } from 'lucide-react';

const BugIssues = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { bugReportsData } = useSelector(state => state?.superadminReducer);

    const [filters, setFilters] = useState({ status: '', priority: '', module: '', search: '' });

    useEffect(() => {
        dispatch(SuperadminActions.getBugReports(filters));
    }, []);

    const handleFilter = () => {
        dispatch(SuperadminActions.getBugReports(filters));
    };

    const handleReset = () => {
        const reset = { status: '', priority: '', module: '', search: '' };
        setFilters(reset);
        dispatch(SuperadminActions.getBugReports(reset));
    };

    const handleDelete = (id) => {
        dispatch(SuperadminActions.deleteBugReport({ id }));
    };

    const statusColors = {
        'Open': { bg: '#FEF3C7', color: '#D97706' },
        'In Review': { bg: '#EDE9FE', color: '#7C3AED' },
        'Valid': { bg: '#DBEAFE', color: '#2563EB' },
        'In Progress': { bg: '#CFFAFE', color: '#0891B2' },
        'Testing': { bg: '#E0E7FF', color: '#4F46E5' },
        'Closed': { bg: '#D1FAE5', color: '#059669' },
        'Rejected': { bg: '#FEE2E2', color: '#DC2626' },
    };

    const priorityColors = {
        'Critical': { bg: '#FEE2E2', color: '#DC2626' },
        'High': { bg: '#FEF3C7', color: '#D97706' },
        'Medium': { bg: '#DBEAFE', color: '#2563EB' },
        'Low': { bg: '#D1FAE5', color: '#059669' },
    };

    const modules = ['Eye Scan', 'Reports', 'Payment Gateway', 'Partners', 'Affiliates', 'PDF Reports', 'Plans', 'Admin Panel', 'User Management', 'Authentication', 'Dashboard', 'Other'];

    const getTimeAgo = (dateStr) => {
        const now = new Date();
        const date = new Date(dateStr);
        const diffMs = now - date;
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays > 0) return `${diffDays}d ago`;
        if (diffHrs > 0) return `${diffHrs}h ago`;
        return 'Just now';
    };

    const selectStyle = {
        padding: '10px 14px', borderRadius: '8px', border: '2px solid #E2E8F0',
        fontSize: '14px', outline: 'none', backgroundColor: '#FFF', cursor: 'pointer',
        minWidth: '140px',
    };

    return (
        <>
            <RouteHeader title="All Issues" sub_title="View and manage all bug reports" url="/bug-tracker/report" url_name="Report New Bug" />

            <div style={{ backgroundColor: Color.white, padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                {/* Filters */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
                    <input
                        style={{ ...selectStyle, flex: 1, minWidth: '200px' }}
                        type="search"
                        placeholder="Search by title, bug ID, description..."
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                    />
                    <select style={selectStyle} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                        <option value="">All Status</option>
                        {['Open', 'In Review', 'Valid', 'In Progress', 'Testing', 'Closed', 'Rejected'].map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    <select style={selectStyle} value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
                        <option value="">All Priority</option>
                        {['Critical', 'High', 'Medium', 'Low'].map(p => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                    <select style={selectStyle} value={filters.module} onChange={(e) => setFilters({ ...filters, module: e.target.value })}>
                        <option value="">All Modules</option>
                        {modules.map(m => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                    <div onClick={handleFilter} style={{
                        padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
                        background: 'linear-gradient(to right, #6653E8, #9544EB)', color: '#FFF',
                    }}>
                        Filter
                    </div>
                    <div onClick={handleReset} style={{
                        padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
                        backgroundColor: '#F1F5F9', color: '#64748B',
                    }}>
                        Reset
                    </div>
                </div>

                {/* Results Count */}
                <p style={{ fontSize: '14px', color: '#72727B', marginBottom: '15px' }}>
                    Showing {bugReportsData?.length || 0} issues
                </p>

                {/* Bug List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {bugReportsData && bugReportsData.length > 0 ? bugReportsData.map((bug, idx) => (
                        <div key={bug._id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '16px 20px', borderRadius: '10px', backgroundColor: '#FAFAFA',
                            border: '1px solid #F1F5F9', transition: 'all 0.2s', cursor: 'pointer',
                        }}
                            onClick={() => navigate(`/bug-tracker/issues/${bug._id}`)}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#F0F0FF'; e.currentTarget.style.borderColor = '#C7D2FE'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#FAFAFA'; e.currentTarget.style.borderColor = '#F1F5F9'; }}
                        >
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '12px', color: '#5045E5', fontWeight: '700', fontFamily: 'monospace' }}>{bug.bug_id}</span>
                                    <span style={{
                                        fontSize: '11px', fontWeight: '600', padding: '2px 10px', borderRadius: '20px',
                                        backgroundColor: statusColors[bug.status]?.bg, color: statusColors[bug.status]?.color,
                                    }}>
                                        {bug.status}
                                    </span>
                                    <span style={{
                                        fontSize: '11px', fontWeight: '600', padding: '2px 10px', borderRadius: '20px',
                                        backgroundColor: priorityColors[bug.priority]?.bg, color: priorityColors[bug.priority]?.color,
                                    }}>
                                        {bug.priority}
                                    </span>
                                </div>
                                <p style={{ fontSize: '15px', fontWeight: '500', color: '#1E293B', marginBottom: '4px' }}>{bug.title}</p>
                                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '12px', color: '#72727B' }}>📁 {bug.module}</span>
                                    {bug.affected_user && <span style={{ fontSize: '12px', color: '#72727B' }}>👤 {bug.affected_user}</span>}
                                    <span style={{ fontSize: '12px', color: '#72727B', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <Clock size={12} /> {getTimeAgo(bug.createdAt)}
                                    </span>
                                    {bug.comments?.length > 0 && (
                                        <span style={{ fontSize: '12px', color: '#72727B' }}>💬 {bug.comments.length}</span>
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                                <div onClick={() => navigate(`/bug-tracker/issues/${bug._id}`)} style={{ cursor: 'pointer', color: '#5045E5' }}>
                                    <Eye size={18} />
                                </div>
                                <div onClick={() => handleDelete(bug._id)} style={{ cursor: 'pointer', color: '#EF4444' }}>
                                    <Trash2 size={18} />
                                </div>
                            </div>
                        </div>
                    )) : (
                        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#72727B' }}>
                            <p style={{ fontSize: '18px', marginBottom: '10px' }}>No issues found</p>
                            <p style={{ fontSize: '14px' }}>Try adjusting filters or report a new bug</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BugIssues;
